import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import {
  createSupportConsoleService,
  type SupportConsoleService,
} from './supportConsoleService';
import type {
  SupportAccess,
  SupportRemoval,
  SupportUserRecord,
  SupportUserSummary,
} from './supportConsoleTypes';

// Support answers carry other people's personal data, so none of them outlive
// the console: gcTime 0 drops a result the moment nothing on screen shows it,
// and staleTime 0 means a result is never reused in place of asking again.
// Nothing here writes to the address bar or to browser storage.
const NEVER_KEPT = { gcTime: 0, staleTime: 0 } as const;

export const supportConsoleKeys = {
  access: (identity: string) => ['support-console', 'access', identity] as const,
  accessAll: ['support-console', 'access'] as const,
  all: ['support-console'] as const,
  // `run` counts the searches asked for, so that asking for the same thing
  // twice is two searches and not one remembered answer.
  search: (query: string, run = 0) => ['support-console', 'search', query, run] as const,
  searchAll: ['support-console', 'search'] as const,
  user: (userId: string) => ['support-console', 'user', userId] as const,
};

// A 403 or a 404 from a support call can mean access has gone since the page
// opened: the list changed, or the sign-in got too old.
export function isSupportRefusal(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) {
    return false;
  }

  const status = (error as Partial<ApiError>).status;
  return status === 403 || status === 404;
}

// freshToken: ask Firebase for a new ID token instead of using the one in
// hand. The backend answers an expired token exactly as it answers a stranger,
// with "not found", so the client's usual "refresh on 401" never happens here;
// a computer whose clock runs slow would otherwise lose the console for the
// last moments of every hour.
function useSupportService({ freshToken = false } = {}): () => SupportConsoleService {
  const location = useLocation();
  const navigate = useNavigate();
  const { getIdToken, user } = useAuth();

  return () => {
    if (!user) {
      throw new Error('You must be signed in.');
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;

    return createSupportConsoleService(
      createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken: freshToken ? () => getIdToken(true) : getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true }),
      }),
    );
  };
}

// Runs a support call. When the server refuses it, access is asked about
// again, and the gate decides what the person sees.
function useSupportCall() {
  const queryClient = useQueryClient();
  const createService = useSupportService();

  return async function call<T>(
    run: (service: SupportConsoleService) => Promise<T>,
  ): Promise<T> {
    try {
      return await run(createService());
    } catch (error) {
      if (isSupportRefusal(error)) {
        void queryClient.invalidateQueries({ queryKey: supportConsoleKeys.accessAll });
      }

      throw error;
    }
  };
}

// Asks the backend whether the signed-in person may use the console. This is
// the only thing that decides it: nothing in the browser can. It always asks
// with a newly issued token, so the answer is about the person, not about how
// old the token in hand happened to be.
export function useSupportConsoleAccess() {
  const { user } = useAuth();
  const createService = useSupportService({ freshToken: true });

  return useQuery<SupportAccess, ApiError>({
    ...NEVER_KEPT,
    enabled: Boolean(user),
    queryFn: () => createService().checkAccess(),
    queryKey: supportConsoleKeys.access(user?.uid ?? ''),
    // A refusal is an answer. Only a dropped connection is worth one more try.
    retry: (failures, error) => error?.kind === 'network' && failures < 1,
  });
}

// Finds users whose ID or profile email is the query. Runs only once there is
// a query: the page sets it, and counts the run, when the form is submitted.
export function useSupportUserSearch(query: string, run = 0) {
  const call = useSupportCall();

  return useQuery<SupportUserSummary[], ApiError>({
    ...NEVER_KEPT,
    enabled: query !== '',
    queryFn: () => call((service) => service.searchUsers(query)),
    queryKey: supportConsoleKeys.search(query, run),
    retry: false,
  });
}

export function useSupportUser(userId: string | null) {
  const call = useSupportCall();

  return useQuery<SupportUserRecord, ApiError>({
    ...NEVER_KEPT,
    enabled: Boolean(userId),
    queryFn: () => call((service) => service.getUser(userId ?? '')),
    queryKey: supportConsoleKeys.user(userId ?? ''),
    retry: false,
  });
}

// Removes a student from a course, then loads the user again so the page
// shows what the record says now.
export function useRemoveStudent() {
  const call = useSupportCall();
  const queryClient = useQueryClient();

  return useMutation<SupportRemoval, ApiError, { courseId: string; userId: string }>({
    // Like the queries: the removal's user and course are not kept once
    // nothing on screen is showing its outcome.
    gcTime: 0,
    mutationFn: (input) => call((service) => service.removeStudent(input)),
    onSuccess: async (_removal, input) => {
      // A search result's course count is out of date now.
      void queryClient.invalidateQueries({ queryKey: supportConsoleKeys.searchAll });
      await queryClient.invalidateQueries({ queryKey: supportConsoleKeys.user(input.userId) });
    },
  });
}
